import { useEffect, useMemo, useState } from 'react';
import { useSetAtom, useAtomValue } from 'jotai';
import {
  selectedTemplateField as selectedAssemblyField,
  selectedTemplateId,
  allPOITypes,
} from '../state';
import { PageLoader } from '../../shared/PageLoader';
import { assemblyTabStyles, FloatingLine } from './styles';
import { BasicTabs } from '@/components/shared/BasicTabs';
import { AssemblyFieldsPanel } from './AssemblyFieldsPanel';
import { ReviewPanelConfigForm } from './ReviewPanelConfigForm';
import { ReviewActionChecklistConfigForm } from './ReviewActionChecklistConfigForm';
import { TemplateEngineKit } from './TemplateEngineKit';
import { useFeatureFlag } from '@/hooks';
import { useAllPointOfInterestTemplateQuery } from '@/__generated__/graphql';
import { EquipmentDetailsForm } from './EquipmentDetailsForm';

export const AssemblyTemplateForm = ({
  emptyForm,
  handleEmptyForm,
  structureId,
}: {
  emptyForm: boolean;
  handleEmptyForm: (value: boolean) => void;
  structureId: string;
}) => {
  const setAllPointOfInterestTemplates = useSetAtom(allPOITypes);
  const selectedAssemblyTemplateId = useAtomValue(selectedTemplateId);
  const [selectedTab, setSelectedTab] = useState<number>(0);
  const selectedField = useAtomValue(selectedAssemblyField);
  const workInProgress = useFeatureFlag('work-in-progress');

  const { data: poiTemplatesData } = useAllPointOfInterestTemplateQuery({
    variables: {
      structureId,
      assemblyTemplateId: selectedAssemblyTemplateId || '',
    },
    skip: !structureId || !selectedAssemblyTemplateId,
  });

  useEffect(() => {
    if (poiTemplatesData?.allPointOfInterestTemplate) {
      setAllPointOfInterestTemplates(poiTemplatesData?.allPointOfInterestTemplate ?? []);
    }
  }, [poiTemplatesData?.allPointOfInterestTemplate, setAllPointOfInterestTemplates]);

  const handleTabChange = (selectedTabIndex: number) => {
    setSelectedTab(selectedTabIndex);
  };

  const tabs = useMemo(() => {
    return [
      {
        index: 0,
        label: 'Fields',
        component: (
          <AssemblyFieldsPanel
            emptyForm={emptyForm}
            handleEmptyForm={handleEmptyForm}
            structureId={structureId}
          />
        ),
      },
      {
        index: 1,
        label: 'Review Panel Configuration',
        component: <ReviewPanelConfigForm structureId={structureId} />,
      },
      {
        index: 2,
        label: 'Review Action Checklist Config',
        component: <ReviewActionChecklistConfigForm structureId={structureId} />,
      },
      {
        index: 3,
        label: 'Equipment Details Side Panel',
        component: <EquipmentDetailsForm structureId={structureId} />,
      },
      ...(workInProgress
        ? [
            {
              index: 4,
              label: 'Template Engine Kit',
              component: <TemplateEngineKit structureId={structureId} />,
            },
          ]
        : []),
    ];
  }, [emptyForm, handleEmptyForm, structureId, workInProgress]);

  if (!selectedField) return <PageLoader />;

  return (
    <BasicTabs
      tabs={tabs}
      selectedTab={selectedTab}
      changeTab={handleTabChange}
      tabsStyles={assemblyTabStyles}
      customSeperator={<FloatingLine />}
      renderingStrategy="eager"
    />
  );
};
